import React, { useState, useEffect, createContext, useRef, useCallback } from "react";

export const PlayerContext = createContext();

export const PlayerProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlayerError, setIsPlayerError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [formattedTime, setFormattedTime] = useState("00:00");
  const [formattedDuration, setFormattedDuration] = useState("00:00");
  const [showFullPlayer, setShowFullPlayer] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);

  const playerRef = useRef(null);
  const timeUpdateInterval = useRef(null);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const updateTimeDisplay = useCallback(() => {
    if (playerRef.current && playerRef.current.getCurrentTime) {
      const time = playerRef.current.getCurrentTime();
      setCurrentTime(time);
      setFormattedTime(formatTime(time));
    }
  }, []);

  const startTimeUpdate = useCallback(() => {
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
    }
    timeUpdateInterval.current = setInterval(updateTimeDisplay, 1000);
  }, [updateTimeDisplay]);

  const stopTimeUpdate = useCallback(() => {
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
      timeUpdateInterval.current = null;
    }
  }, []);

  const setPlayer = useCallback((player) => {
    playerRef.current = player;
    if (player && player.getDuration) {
      const dur = player.getDuration();
      setDuration(dur);
      setFormattedDuration(formatTime(dur));
    }
  }, []);

  const play = useCallback(() => {
    if (playerRef.current?.playVideo) {
      playerRef.current.playVideo();
    }
  }, []);

  const pause = useCallback(() => {
    if (playerRef.current?.pauseVideo) {
      playerRef.current.pauseVideo();
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const seekTo = useCallback((time) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(time);
      setCurrentTime(time);
      setFormattedTime(formatTime(time));
    }
  }, []);

  const seekByPercentage = useCallback((percentage) => {
    const newTime = (percentage / 100) * duration;
    seekTo(newTime);
  }, [duration, seekTo]);

  const setVolumeLevel = useCallback((vol) => {
    const clampedVolume = Math.max(0, Math.min(100, vol));
    setVolume(clampedVolume);
    if (playerRef.current?.setVolume) {
      try {
        playerRef.current.setVolume(clampedVolume);
        console.log("Volume set to:", clampedVolume);
      } catch (error) {
        console.warn("Volume control not available on this device:", error);
      }
    }
    if (clampedVolume > 0 && playerRef.current?.unMute) {
      playerRef.current.unMute();
      setIsMuted(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (playerRef.current?.isMuted && playerRef.current?.mute && playerRef.current?.unMute) {
      const currentlyMuted = playerRef.current.isMuted();
      if (currentlyMuted) {
        playerRef.current.unMute();
        setIsMuted(false);
      } else {
        playerRef.current.mute();
        setIsMuted(true);
      }
    }
  }, []);

  useEffect(() => {
    if (isPlaying) {
      startTimeUpdate();
    } else {
      stopTimeUpdate();
    }
    return () => stopTimeUpdate();
  }, [isPlaying, startTimeUpdate, stopTimeUpdate]);

  return (
    <PlayerContext.Provider
      value={{
        // State
        isPlaying,
        setIsPlaying,
        isPlayerReady,
        setIsPlayerReady,
        isPlayerError,
        setIsPlayerError,
        currentTime,
        duration,
        formattedTime,
        formattedDuration,
        showFullPlayer,
        setShowFullPlayer,
        volume,
        isMuted,
        // Refs
        playerRef,
        // Methods
        setPlayer,
        setDuration: (dur) => {
          setDuration(dur);
          setFormattedDuration(formatTime(dur));
        },
        play,
        pause,
        togglePlay,
        seekTo,
        seekByPercentage,
        formatTime,
        setVolumeLevel,
        toggleMute,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerContext;
